import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useChefsLab } from '../context/ChefsLabContext';
import { supabase } from '../lib/supabase';
import CardSelector from '../components/chefslab/CardSelector';
import ListingCard from '../components/chefslab/ListingCard';
import SettingsWheel from '../components/chefslab/SettingsWheel';
import ReportModal from '../components/chefslab/ReportModal';
import '../styles/gameboy.css';
import ChefImage from '../assets/images/Chef.png';
import { getFullCardPricing, transformForChefsLab, getMultiSourcePricing, formatPriceDetail, fetchPoketraceListings, RESEARCH_CACHE_VERSION } from '../utils/poketraceService';

// ============================================
// CHEF'S LAB - Gemini-Powered Research
// ============================================
// 3-Zone UI:
// Zone A: Condition Switcher (Raw vs Graded)
// Zone B: Price Matrix (Grades 1-10)
// Zone C: Chef's Verdict (Market Insight + ROI)

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Grading companies
const COMPANIES = ['psa', 'cgc', 'bgs', 'sgc', 'tag', 'ace'];
const COMPANY_LABELS = { psa: 'PSA', bgs: 'BGS', cgc: 'CGC', sgc: 'SGC', tag: 'TAG', ace: 'ACE' };
const GRADES = ['10', '9', '8', '7', '6', '5', '4', '3', '2', '1'];

// Raw condition labels
const RAW_CONDITIONS = [
  { id: 'nm', label: 'NM', full: 'Near Mint' },
  { id: 'lp', label: 'LP', full: 'Lightly Played' },
  { id: 'mp', label: 'MP', full: 'Moderately Played' },
  { id: 'hp', label: 'HP', full: 'Heavily Played' },
  { id: 'dmg', label: 'DMG', full: 'Damaged' },
];

// Cache freshness (14 days in ms)
const CACHE_FRESHNESS_MS = 14 * 24 * 60 * 60 * 1000;

// Format price
const formatPrice = (price) => {
  if (price === null || price === undefined) return '--';
  if (price >= 1000) {
    return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date as MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return 'No date';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return dateStr;
  }
};

// ============================================
// ICONS
// ============================================

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
    <path d="M16 21h5v-5" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// ============================================
// MAIN COMPONENT
// ============================================

const ChefsLab = () => {
  const { theme } = useTheme();
  const { activeProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { cardId } = useParams();

  // Get context for demo mode
  const {
    selectedCard: contextCard,
    researchData: contextResearchData,
    setResearchData: setContextResearchData,
    clearSelectedCard,
    preferences,
    setSelectedTimePeriod: setContextTimePeriod,
    calculateAverage: contextCalculateAverage,
    getDisplayListings: contextGetDisplayListings,
    rawSources: contextRawSources,
    setRawSources: setContextRawSources,
    rawSourcesLoading: contextRawSourcesLoading,
    setRawSourcesLoading: setContextRawSourcesLoading,
  } = useChefsLab();

  // Demo mode: no cardId in URL means standalone demo
  const isDemoMode = !cardId;

  // Card from navigation state OR context (demo mode) OR fetched by ID
  const navCard = location.state?.card;
  const [fetchedCard, setFetchedCard] = useState(null);
  const card = isDemoMode ? contextCard : (navCard || fetchedCard);

  // Fetch card from Supabase if we have cardId but no nav state (direct URL / refresh)
  useEffect(() => {
    if (cardId && !navCard && !fetchedCard) {
      supabase.from('cards').select('*').eq('id', cardId).maybeSingle()
        .then(({ data }) => { if (data) setFetchedCard(data); });
    }
  }, [cardId, navCard, fetchedCard]);

  // Card properties - handle both demo cards table format and regular cards table format
  const cardName = card?.english_name || card?.card_name || card?.name || '';
  const setNumber = card?.tcg_local_id || card?.set_number || card?.card_number || card?.number || '';
  const setName = card?.set_name || '';
  const cardImageUrl = card?.digital_image_url || card?.original_image_url || '';

  // Build TCGdex card ID for API lookups (format: "dp3-3" or "sv1-25")
  const tcgdexCardId = card?.card_id ||
    (card?.tcg_set_id && card?.tcg_local_id ? `${card.tcg_set_id}-${card.tcg_local_id}` : null);
  const cardRarity = card?.rarity || '';
  const cardVariant = card?.variant || 'Normal';

  // ============================================
  // STATE
  // ============================================

  const [phase, setPhase] = useState('loading'); // loading, no_data, display, researching
  const [viewMode, setViewMode] = useState('raw'); // 'raw' | 'graded'
  const [selectedCompany, setSelectedCompany] = useState('psa');
  const [expandedGrade, setExpandedGrade] = useState(null); // Which grade is expanded
  const [timePeriod, setTimePeriod] = useState('3m'); // '3m' | '6m' | '12m'

  // Research data
  const [researchData, setResearchData] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const [isFresh, setIsFresh] = useState(false);

  // Loading state
  const [isResearching, setIsResearching] = useState(false);
  const [researchStartedAt, setResearchStartedAt] = useState(null);
  const [showDataInfo, setShowDataInfo] = useState(false);

  // Multi-source raw pricing - use context in demo mode so Compare page can access
  const [localRawSources, setLocalRawSources] = useState(null);
  const [localRawSourcesLoading, setLocalRawSourcesLoading] = useState(false);
  const [rawSubTab, setRawSubTab] = useState('summary');

  const rawSources = isDemoMode ? contextRawSources : localRawSources;
  const setRawSources = isDemoMode ? setContextRawSources : setLocalRawSources;
  const rawSourcesLoading = isDemoMode ? contextRawSourcesLoading : localRawSourcesLoading;
  const setRawSourcesLoading = isDemoMode ? setContextRawSourcesLoading : setLocalRawSourcesLoading;

  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [listingToReport, setListingToReport] = useState(null);
  const [cardExpanded, setCardExpanded] = useState(false);
  const [showRefreshPopover, setShowRefreshPopover] = useState(false);

  // Redirect if no card (only for non-demo mode)
  // In demo mode, we show CardSelector instead of redirecting
  useEffect(() => {
    if (!card && !isDemoMode) {
      if (cardId) {
        navigate(`/card/${cardId}`, { replace: true });
      } else {
        navigate('/collection', { replace: true });
      }
    }
  }, [card, cardId, isDemoMode, navigate]);

  // ============================================
  // CARD IDENTIFIER
  // ============================================

  const buildIdentifier = useCallback((name, set, num) => {
    const pokemonName = name
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')[0]
      .toLowerCase();

    return [pokemonName, set, num]
      .filter(Boolean)
      .join('_')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }, []);

  // ============================================
  // CHECK CACHE
  // ============================================

  const checkCache = useCallback(async () => {
    if (!cardName) return null;

    const identifier = buildIdentifier(cardName, setName, setNumber);
    console.log('[ChefsLab] Checking cache for:', identifier);

    const { data, error } = await supabase
      .from('card_research_cache_test')
      .select('*')
      .eq('card_identifier', identifier)
      .maybeSingle();

    if (error) {
      console.error('[ChefsLab] Cache check error:', error);
      return null;
    }

    if (data) {
      const versionMatch = data.research_version === `v${RESEARCH_CACHE_VERSION}`;
      if (!versionMatch) {
        console.log(`[ChefsLab] Cache version mismatch (${data.research_version} != v${RESEARCH_CACHE_VERSION}), treating as stale`);
        return { data, fresh: false };
      }
      const lastResearched = new Date(data.last_researched_at);
      const daysSince = (Date.now() - lastResearched.getTime()) / CACHE_FRESHNESS_MS;
      const fresh = daysSince < 1; // Less than 14 days

      console.log(`[ChefsLab] Cache found v${RESEARCH_CACHE_VERSION}, ${Math.floor(daysSince * 14)} days old, fresh: ${fresh}`);

      return { data, fresh };
    }

    return null;
  }, [cardName, setName, setNumber, buildIdentifier]);

  // ============================================
  // FETCH RESEARCH (Call Edge Function)
  // ============================================

  // Fire-and-forget research - runs in background, user can navigate away
  const startResearch = useCallback(() => {
    if (!cardName) return;

    setIsResearching(true);
    setResearchStartedAt(new Date());
    setPhase('researching');

    console.log('[ChefsLab] Starting background research...');

    // Fire and forget - don't await
    fetch(`${SUPABASE_URL}/functions/v1/gemini-card-research`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        cardName,
        setName,
        setNumber,
        cardImageUrl,
        cardId: card?.id,
      }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        return response.json();
      })
      .then(async (result) => {
        if (!result.success) throw new Error(result.error || 'Research failed');

        // Save to cache
        const identifier = buildIdentifier(cardName, setName, setNumber);
        await supabase
          .from('card_research_cache_test')
          .upsert({
            card_identifier: identifier,
            card_id: card?.id,
            card_name: cardName,
            set_name: setName,
            set_number: setNumber,
            card_image_url: cardImageUrl,
            raw_data: result.data.raw_data,
            graded_data: result.data.graded_data,
            evidence: result.data.evidence,
            chef_insight: result.data.chef_insight,
            research_version: `v${RESEARCH_CACHE_VERSION}`,
            last_researched_at: new Date().toISOString(),
            created_by: activeProfile?.id,
          }, { onConflict: 'card_identifier' });

        console.log('[ChefsLab] Research complete and cached!');

        // Log the results
        console.log('\n' + '='.repeat(60));
        console.log('[ChefsLab] FRESH RESEARCH RESULTS FOR:', cardName);
        console.log('='.repeat(60));
        console.log('\n[ChefsLab] RAW PRICES:');
        console.table(result.data.raw_data);
        console.log('\n[ChefsLab] GRADED PRICES:');
        console.log('  PSA:', result.data.graded_data.psa);
        console.log('  BGS:', result.data.graded_data.bgs);
        console.log('  CGC:', result.data.graded_data.cgc);
        console.log('  SGC:', result.data.graded_data.sgc);
        console.log('  TAG:', result.data.graded_data.tag);
        console.log('  ACE:', result.data.graded_data.ace);
        console.log('\n[ChefsLab] CHEF INSIGHT:');
        console.log('  ', result.data.chef_insight || '(none)');
        console.log('\n[ChefsLab] EVIDENCE URLs:', result.data.evidence?.length || 0);
        if (result.data.evidence && result.data.evidence.length > 0) {
          result.data.evidence.forEach((e, i) => {
            console.log(`  ${i + 1}. ${e.grade} - $${e.price} - ${e.url}`);
          });
        }
        console.log('='.repeat(60) + '\n');

        // Only update UI if still on this page
        setResearchData(result.data);
        setCachedAt(new Date());
        setIsFresh(true);
        setPhase('display');
        setIsResearching(false);
      })
      .catch((error) => {
        console.error('[ChefsLab] Research error:', error);
        setIsResearching(false);
        // Don't change phase - let user retry
      });
  }, [cardName, setName, setNumber, cardImageUrl, card?.id, activeProfile?.id, buildIdentifier]);

  // ============================================
  // V2 RESEARCH - TWO-PART PROGRESSIVE LOADING
  // ============================================
  // Part 1: PSA + CGC (fast, show immediately)
  // Part 2: BGS/SGC + ACE/TAG + Raw + Chef Verdict

  const [researchPart, setResearchPart] = useState(0); // 0 = not started, 1 = part1, 2 = part2

  const startResearchV2 = useCallback(async () => {
    if (!cardName) return;

    setIsResearching(true);
    setResearchStartedAt(new Date());
    setPhase('researching');
    setResearchPart(1);

    console.log('[ChefsLab V2] Starting 2-Part research...');
    console.log('[ChefsLab V2] Card:', cardName);
    console.log('[ChefsLab V2] Set:', setName, '#', setNumber);

    try {
      // ============================================
      // PART 1: PSA + CGC
      // ============================================
      console.log('\n[ChefsLab V2] PART 1: PSA + CGC...');

      const part1Response = await fetch(`${SUPABASE_URL}/functions/v1/research-card-v2-part1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ cardName, setName, setNumber, cardId: card?.id, tcgdexCardId, rarity: cardRarity }),
      });

      if (!part1Response.ok) throw new Error(`Part 1 API error: ${part1Response.status}`);
      const part1Result = await part1Response.json();
      if (!part1Result.success) throw new Error(part1Result.error || 'Part 1 failed');

      console.log('[ChefsLab V2] Part 1 complete!');
      console.log('[ChefsLab V2] PSA + CGC listings:', part1Result.data.evidence?.length || 0);
      console.log('[ChefsLab V2] Duration:', part1Result.metadata?.duration, 'seconds');

      // Show Part 1 data immediately
      const part1Data = {
        raw_data: {},
        graded_data: {
          psa: part1Result.data.graded_data?.psa || {},
          cgc: part1Result.data.graded_data?.cgc || {},
          bgs: {}, sgc: {}, ace: {}, tag: {}
        },
        evidence: part1Result.data.evidence || [],
        chef_insight: 'Researching more companies...',
      };
      setResearchData(part1Data);
      setPhase('display'); // Show what we have!
      setResearchPart(2);

      // ============================================
      // PART 2: BGS/SGC + ACE/TAG + Raw + Verdict
      // ============================================
      console.log('\n[ChefsLab V2] PART 2: BGS/SGC + ACE/TAG + Raw...');

      const part2Response = await fetch(`${SUPABASE_URL}/functions/v1/research-card-v2-part2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          cardName,
          setName,
          setNumber,
          cardId: card?.id,
          existingEvidence: part1Result.data.evidence || [],
        }),
      });

      if (!part2Response.ok) throw new Error(`Part 2 API error: ${part2Response.status}`);
      const part2Result = await part2Response.json();
      if (!part2Result.success) throw new Error(part2Result.error || 'Part 2 failed');

      console.log('\n' + '='.repeat(60));
      console.log('[ChefsLab V2] RESEARCH COMPLETE');
      console.log('='.repeat(60));
      console.log('[ChefsLab V2] Total listings:', part2Result.data.evidence?.length || 0);
      console.log('[ChefsLab V2] Part 2 duration:', part2Result.metadata?.duration, 'seconds');
      console.log('[ChefsLab V2] Chef Insight:', part2Result.data.chef_insight);

      // Log all graded prices
      console.log('\n[ChefsLab V2] FINAL GRADED PRICES:');
      ['psa', 'cgc', 'bgs', 'sgc', 'ace', 'tag'].forEach(company => {
        const prices = part2Result.data.graded_data[company] || {};
        const filled = Object.entries(prices).filter(([_, v]) => v !== null);
        if (filled.length > 0) {
          console.log(`  ${company.toUpperCase()}:`, Object.fromEntries(filled));
        }
      });
      console.log('='.repeat(60) + '\n');

      // Save complete data to cache
      const identifier = buildIdentifier(cardName, setName, setNumber);
      await supabase
        .from('card_research_cache_test')
        .upsert({
          card_identifier: identifier,
          card_id: card?.id,
          card_name: cardName,
          set_name: setName,
          set_number: setNumber,
          card_image_url: cardImageUrl,
          raw_data: part2Result.data.raw_data,
          graded_data: part2Result.data.graded_data,
          evidence: part2Result.data.evidence,
          chef_insight: part2Result.data.chef_insight,
          last_researched_at: new Date().toISOString(),
          created_by: activeProfile?.id,
          research_version: `v${RESEARCH_CACHE_VERSION}`,
          usage: part2Result.usage || {},
          research_metadata: part2Result.metadata || {},
        }, { onConflict: 'card_identifier' });

      console.log('[ChefsLab V2] Saved to cache!');

      // Update UI with complete data
      setResearchData(part2Result.data);
      setCachedAt(new Date());
      setIsFresh(true);
      setIsResearching(false);
      setResearchPart(0);

    } catch (error) {
      console.error('[ChefsLab V2] Research error:', error);
      setIsResearching(false);
      setResearchPart(0);
      // If we have Part 1 data, keep showing it
      if (researchData?.evidence?.length > 0) {
        setPhase('display');
      } else {
        setPhase('no_data');
      }
    }
  }, [cardName, setName, setNumber, cardImageUrl, card?.id, activeProfile?.id, buildIdentifier, researchData]);

  // ============================================
  // V3 RESEARCH - Countdown API (Direct eBay Sold)
  // ============================================

  const startResearchV3 = useCallback(async () => {
    if (!cardName) return;

    setIsResearching(true);
    setResearchStartedAt(new Date());
    setPhase('researching');

    console.log('[ChefsLab V3] Starting Countdown API research...');
    console.log('[ChefsLab V3] Card:', cardName);
    console.log('[ChefsLab V3] Set:', setName, '#', setNumber);
    console.log('[ChefsLab V3] TCGdex ID:', tcgdexCardId);
    console.log('[ChefsLab V3] Rarity:', cardRarity);

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/research-card-v3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ cardName, setName, setNumber, cardId: card?.id, tcgdexCardId, rarity: cardRarity }),
      });

      if (!response.ok) throw new Error(`V3 API error: ${response.status}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'V3 failed');

      console.log('\n' + '='.repeat(60));
      console.log('[ChefsLab V3] RESEARCH COMPLETE');
      console.log('='.repeat(60));
      console.log('[ChefsLab V3] Total listings:', result.data.evidence?.length || 0);
      console.log('[ChefsLab V3] Duration:', result.metadata?.duration, 'seconds');
      console.log('[ChefsLab V3] API calls:', result.usage?.countdown?.calls || 0);
      console.log('[ChefsLab V3] Estimated cost: $', result.usage?.total?.estimatedCost?.toFixed(4) || 0);
      console.log('[ChefsLab V3] Chef Insight:', result.data.chef_insight);

      // Log graded prices
      console.log('\n[ChefsLab V3] GRADED PRICES:');
      ['psa', 'cgc', 'bgs', 'sgc', 'ace', 'tag'].forEach(company => {
        const prices = result.data.graded_data[company] || {};
        const filled = Object.entries(prices).filter(([_, v]) => v !== null);
        if (filled.length > 0) {
          console.log(`  ${company.toUpperCase()}:`, Object.fromEntries(filled));
        }
      });
      console.log('='.repeat(60) + '\n');

      // Save to cache
      const identifier = buildIdentifier(cardName, setName, setNumber);
      await supabase
        .from('card_research_cache_test')
        .upsert({
          card_identifier: identifier,
          card_id: card?.id,
          card_name: cardName,
          set_name: setName,
          set_number: setNumber,
          card_image_url: cardImageUrl,
          raw_data: result.data.raw_data,
          graded_data: result.data.graded_data,
          evidence: result.data.evidence,
          chef_insight: result.data.chef_insight,
          last_researched_at: new Date().toISOString(),
          created_by: activeProfile?.id,
          research_version: `v${RESEARCH_CACHE_VERSION}`,
          usage: result.usage || {},
          research_metadata: result.metadata || {},
        }, { onConflict: 'card_identifier' });

      console.log('[ChefsLab V3] Saved to cache!');

      // Update UI
      setResearchData(result.data);
      setCachedAt(new Date());
      setIsFresh(true);
      setPhase('display');
      setIsResearching(false);

    } catch (error) {
      console.error('[ChefsLab V3] Research error:', error);
      setIsResearching(false);
      setPhase('no_data');
    }
  }, [cardName, setName, setNumber, cardImageUrl, card?.id, activeProfile?.id, buildIdentifier]);

  // ============================================
  // FETCH FROM POKETRACE
  // ============================================

  const fetchPoketraceData = useCallback(async (skipListings = false) => {
    if (!cardName) return null;

    console.log('[ChefsLab] Fetching Poketrace data for:', cardName, setName, setNumber, skipListings ? '(skipping listings)' : '');

    const result = await getFullCardPricing({
      cardName,
      cardNumber: setNumber,
      setName,
      variant: cardVariant,
    });

    if (!result) {
      console.log('[ChefsLab] No Poketrace data found');
      return null;
    }

    const { priceData, poketraceId } = result;

    let listings = [];

    if (!skipListings) {
      // Check cache for existing listings first (e.g. from Fair Trade)
      const identifier = buildIdentifier(cardName, setName, setNumber);
      try {
        const { data: cached } = await supabase
          .from('card_research_cache_test')
          .select('raw_sources, research_version')
          .eq('card_identifier', identifier)
          .maybeSingle();

        if (cached?.raw_sources?.listings?.length > 0 && cached.research_version === `v${RESEARCH_CACHE_VERSION}`) {
          listings = cached.raw_sources.listings;
          console.log('[ChefsLab] Using', listings.length, 'cached listings');
        }
      } catch (err) {
        console.error('[ChefsLab] Listings cache check error:', err);
      }

      // Only fetch from API if no cached listings
      if (listings.length === 0 && poketraceId) {
        try {
          listings = await fetchPoketraceListings(poketraceId);
          console.log('[ChefsLab] Fetched', listings.length, 'individual listings from API');
        } catch (err) {
          console.error('[ChefsLab] Listings fetch error:', err);
        }
      }
    }

    const transformed = transformForChefsLab(priceData, listings);
    if (!transformed) return null;

    console.log('[ChefsLab] Poketrace data transformed:',
      Object.keys(transformed.graded_data).length, 'companies,',
      transformed.evidence.length, 'evidence entries',
      skipListings ? '(skipped listings - using nav state)' : listings.length > 0 ? '(individual listings)' : '(aggregate tiers)');

    return transformed;
  }, [cardName, setName, setNumber, buildIdentifier]);

  // ============================================
  // INITIAL LOAD
  // ============================================

  useEffect(() => {
    const init = async () => {
      // 0. Check if Fair Trade passed pricing data via nav state (instant, no network)
      const ftPricingResult = location.state?.ftPricingResult;
      const hasNavStateData = !!(ftPricingResult?.gemini);
      if (hasNavStateData) {
        console.log('--- STEP 5 --- Chef\'s Lab using Fair Trade pricing from nav state (NO network, skipping loadMultiSource)');
        const rawSourcesFromFT = {
          poketrace: ftPricingResult.poketrace,
          tcgdex: ftPricingResult.tcgdex,
          justTCG: ftPricingResult.justTCG || null,
          gemini: ftPricingResult.gemini,
          bestPrice: ftPricingResult.bestPrice,
          listings: [],
          fetchedAt: new Date().toISOString(),
        };
        setRawSources(rawSourcesFromFT);
      }

      // 1. Try Poketrace first (fast, uses poketrace_price_cache)
      // Skip expensive listings fetch if we already have data from nav state
      const poketraceData = await fetchPoketraceData(hasNavStateData);

      if (poketraceData) {
        const data = {
          raw_data: poketraceData.raw_data || {},
          graded_data: poketraceData.graded_data || {},
          evidence: poketraceData.evidence || [],
          chef_insight: '',
        };

        console.log('--- STEP 5 --- Chef\'s Lab loaded Poketrace data for:', cardName);

        setResearchData(data);
        setCachedAt(new Date());
        setIsFresh(true);
        setPhase('display');

        // Only call loadMultiSource if we DON'T already have data from nav state
        if (!hasNavStateData) {
          loadMultiSource();
        }
        return;
      }

      // 2. Fallback: check old research cache
      const cached = await checkCache();

      if (cached) {
        const data = {
          raw_data: cached.data.raw_data || {},
          graded_data: cached.data.graded_data || {},
          evidence: cached.data.evidence || [],
          chef_insight: cached.data.chef_insight || '',
        };

        if (cached.data.raw_sources) {
          setRawSources(cached.data.raw_sources);
        }

        console.log('[ChefsLab] LOADED from research cache:', cardName);
        setResearchData(data);
        setCachedAt(new Date(cached.data.last_researched_at));
        setIsFresh(cached.fresh);
        setPhase('display');

        // If no cached multi-source AND no nav state data, fetch it now
        if (!cached.data.raw_sources && !hasNavStateData) {
          loadMultiSource();
        }
      } else {
        console.log('[ChefsLab] No data for:', cardName);
        setPhase('no_data');
      }
    };

    if (cardName) {
      init();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardName]);

  // ============================================
  // MULTI-SOURCE RAW PRICING
  // ============================================

  const loadMultiSource = useCallback(async (force = false) => {
    if (!force && (rawSources || rawSourcesLoading)) {
      console.log('--- STEP 6 --- Using saved data only (rawSources already loaded, no fetch)');
      return;
    }
    if (force) {
      setRawSources(null);
    }

    const identifier = buildIdentifier(cardName, setName, setNumber);

    // Check cache first (unless forcing)
    if (!force) {
      try {
        const { data } = await supabase
          .from('card_research_cache_test')
          .select('raw_sources, research_version')
          .eq('card_identifier', identifier)
          .maybeSingle();

        if (data?.raw_sources && data.research_version === `v${RESEARCH_CACHE_VERSION}`) {
          // Full cache hit (has gemini data) -- use directly, skip Gemini re-call
          if (data.raw_sources.gemini) {
            console.log('--- STEP 5 --- Chef\'s Lab raw_sources CACHE HIT (gemini cached, skipping re-call)');
            setRawSources(data.raw_sources);
            return;
          }
          // Has raw_sources but no gemini -- still use what we have
          console.log('--- STEP 5 --- Chef\'s Lab raw_sources CACHE HIT (no gemini, using as-is)');
          setRawSources(data.raw_sources);
          return;
        }
      } catch (err) {
        console.error('[ChefsLab] raw_sources cache check error:', err);
      }
    }

    setRawSourcesLoading(true);
    console.log('--- STEP 5 WARNING --- Chef\'s Lab CACHE MISS, calling getMultiSourcePricing (NEW Gemini call)...', force ? '(force refresh)' : '');

    try {
      const result = await getMultiSourcePricing({
        cardName,
        cardNumber: setNumber,
        setName,
        tcgdexCardId,
        variant: cardVariant,
      });

      if (result) {
        setRawSources(result);

        // Save to cache with upsert (creates row if it doesn't exist)
        await supabase
          .from('card_research_cache_test')
          .upsert({
            card_identifier: identifier,
            card_name: cardName,
            set_name: setName,
            set_number: setNumber,
            raw_sources: result,
            research_version: `v${RESEARCH_CACHE_VERSION}`,
            last_researched_at: new Date().toISOString(),
          }, { onConflict: 'card_identifier' });

        console.log('--- STEP 5 --- Chef\'s Lab multi-source data saved to cache');
      }
    } catch (err) {
      console.error('[ChefsLab] Multi-source load error:', err);
    }

    setRawSourcesLoading(false);
  }, [rawSources, rawSourcesLoading, cardName, setNumber, setName, tcgdexCardId, cardVariant, buildIdentifier]);


  // ============================================
  // GETTERS
  // ============================================

  const getGradedPrice = (company, grade) => {
    if (!researchData?.graded_data) return null;
    return researchData.graded_data[company]?.[grade];
  };

  const getRawPrice = (condition) => {
    if (!researchData?.raw_data) return null;
    return researchData.raw_data[condition];
  };

  const getHighestGradedValue = () => {
    if (!researchData?.graded_data) return null;

    let highest = { company: null, grade: null, price: 0 };

    for (const company of COMPANIES) {
      for (const grade of GRADES) {
        const price = researchData.graded_data[company]?.[grade];
        if (price && price > highest.price) {
          highest = { company, grade, price };
        }
      }
    }

    return highest.price > 0 ? highest : null;
  };

  // Extract domain from URL for display
  const getDomain = (url) => {
    if (!url) return 'Unknown';
    try {
      const hostname = new URL(url).hostname.replace('www.', '');
      if (hostname.includes('ebay')) return 'eBay';
      if (hostname.includes('pricecharting')) return 'PriceCharting';
      if (hostname.includes('vertexaisearch')) return 'Google';
      return hostname.split('.')[0];
    } catch {
      return 'Link';
    }
  };

  // Filter listings by time period
  const filterByTimePeriod = (listings, period) => {
    if (!listings || listings.length === 0) return listings;

    const now = new Date();
    let cutoffDate;

    switch (period) {
      case '3m':
        cutoffDate = new Date(now.setMonth(now.getMonth() - 3));
        break;
      case '6m':
        cutoffDate = new Date(now.setMonth(now.getMonth() - 6));
        break;
      case '12m':
        cutoffDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      default:
        cutoffDate = new Date(now.setMonth(now.getMonth() - 3));
    }

    return listings.filter(l => {
      if (!l.date) return true; // Include if no date
      const listingDate = new Date(l.date);
      return listingDate >= cutoffDate;
    });
  };

  // Calculate average from listings
  const calculateAverage = (listings) => {
    // In demo mode, use context's calculateAverage which respects cost preferences
    if (isDemoMode && contextCalculateAverage) {
      return contextCalculateAverage(listings);
    }
    // Standard mode: simple average
    if (!listings || listings.length === 0) return null;
    const prices = listings.filter(l => l.price).map(l => l.price);
    if (prices.length === 0) return null;
    return prices.reduce((a, b) => a + b, 0) / prices.length;
  };

  const getEvidenceForGrade = (company, grade) => {
    if (!researchData?.evidence) return [];
    const gradeNum = parseFloat(grade);
    // Match by company and grade_number fields (new format) or by grade string (old format)
    return researchData.evidence.filter(e => {
      // New format with company/grade_number
      if (e.company && e.grade_number !== undefined) {
        const eGrade = typeof e.grade_number === 'number' ? e.grade_number : parseFloat(e.grade_number);
        // Exact match, or for whole grades also match half grades (9 matches 9 and 9.5)
        if (e.company.toLowerCase() !== company.toLowerCase()) return false;
        if (eGrade === gradeNum) return true;
        // Include half grades with their nearest whole grade (9.5 shows under 9)
        if (Number.isInteger(gradeNum) && Math.floor(eGrade) === gradeNum && eGrade !== gradeNum) return true;
        return false;
      }
      // Old format - match by grade string
      return e.grade?.toUpperCase().includes(company.toUpperCase()) &&
             e.grade?.includes(grade);
    });
  };

  // Get filtered listings for a grade based on time period
  const getFilteredListingsForGrade = (company, grade) => {
    const allListings = getEvidenceForGrade(company, grade);
    return filterByTimePeriod(allListings, timePeriod);
  };

  // Get average price for a grade based on time period
  const getAverageForGrade = (company, grade) => {
    const filtered = getFilteredListingsForGrade(company, grade);
    return calculateAverage(filtered);
  };

  // Get all period averages at once for a grade
  const getAllPeriodAverages = (company, grade) => {
    const allListings = getEvidenceForGrade(company, grade);

    // Aggregate-only entries (no individual listings for this grade):
    // use median fields directly for time period columns
    if (allListings.length === 1 && allListings[0].isAggregate && allListings[0].median30d !== undefined) {
      const e = allListings[0];
      return {
        all: e.price,
        '12m': e.median30d,
        '6m': e.median7d,
        '3m': e.median3d,
        count: e.saleCount || 0,
        listings: allListings,
      };
    }

    // Individual listings (or mixed): filter by actual sold dates
    const filterByPeriod = (listings, period) => {
      if (!listings || listings.length === 0) return [];
      if (period === 'all') return listings;

      const now = new Date();
      let cutoffDate;

      switch (period) {
        case '3m':
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        case '6m':
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          break;
        case '12m':
          cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        default:
          return listings;
      }

      return listings.filter(l => {
        if (!l.date) return false;
        const listingDate = new Date(l.date + 'T00:00:00');
        return listingDate >= cutoffDate;
      });
    };

    const all = calculateAverage(allListings);
    const m12 = calculateAverage(filterByPeriod(allListings, '12m'));
    const m6 = calculateAverage(filterByPeriod(allListings, '6m'));
    const m3 = calculateAverage(filterByPeriod(allListings, '3m'));

    return {
      all,
      '12m': m12,
      '6m': m6,
      '3m': m3,
      count: allListings.length,
      listings: allListings
    };
  };

  // Get all listings for raw condition
  const getRawListings = (condition) => {
    if (!researchData?.evidence) return [];
    return researchData.evidence.filter(e => {
      if (e.company === 'raw' && e.grade_number) {
        return e.grade_number.toLowerCase() === condition.toLowerCase();
      }
      return e.grade?.toLowerCase().includes('raw') &&
             e.grade?.toLowerCase().includes(condition.toLowerCase());
    });
  };

  // Get date range from evidence
  const getEvidenceDateRange = () => {
    if (!researchData?.evidence || researchData.evidence.length === 0) return null;

    const dates = researchData.evidence
      .filter(e => e.date)
      .map(e => new Date(e.date + 'T00:00:00'))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a - b);

    if (dates.length === 0) return null;

    const oldest = dates[0];
    const newest = dates[dates.length - 1];
    const monthsDiff = (newest.getFullYear() - oldest.getFullYear()) * 12 +
                       (newest.getMonth() - oldest.getMonth());

    const formatMonth = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

    return {
      oldest: formatMonth(oldest),
      newest: formatMonth(newest),
      monthsSpan: monthsDiff,
      isLimitedRange: monthsDiff < 6 && researchData.evidence.length > 500
    };
  };

  // Calculate ROI (comparing raw NM to graded 10)
  const calculateROI = () => {
    const rawNM = getRawPrice('nm');
    const psa10 = getGradedPrice('psa', '10');

    if (!rawNM || !psa10) return null;

    const profit = psa10 - rawNM;
    const percentage = ((profit / rawNM) * 100).toFixed(0);

    return { profit, percentage, rawNM, psa10 };
  };

  // ============================================
  // RENDER
  // ============================================

  const styles = getStyles(theme);

  // Demo mode: Show CardSelector if no card is selected
  if (isDemoMode && !card) {
    return (
      <div style={styles.container}>
        <CardSelector />
      </div>
    );
  }

  // No card and not in demo mode - return null (redirect will happen via useEffect)
  if (!card) return null;

  const highestValue = getHighestGradedValue();
  const roi = calculateROI();

  return (
    <div style={styles.container}>
      {/* ============================================ */}
      {/* INITIAL LOADING STATE (checking cache) */}
      {/* ============================================ */}
      <AnimatePresence>
        {phase === 'loading' && (
          <motion.div
            style={styles.loadingOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div style={styles.loadingContent}>
              <motion.img
                src={ChefImage}
                alt="Chef"
                style={styles.loadingChef}
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              />
              <div style={styles.loadingTextBox}>
                <p style={styles.loadingText}>Checking for existing research...</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => isDemoMode ? clearSelectedCard() : navigate(-1)}
        >
          <BackIcon />
        </button>

        <div
          style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, cursor: 'pointer' }}
          onClick={() => setCardExpanded(prev => !prev)}
        >
          {cardImageUrl && (
            <img
              src={cardImageUrl}
              alt={cardName}
              style={cardExpanded ? styles.headerCardExpanded : styles.headerCard}
            />
          )}
          <div style={styles.headerInfo}>
            <span style={styles.headerName}>{cardName}</span>
            <span style={styles.headerSet}>{setName} {setNumber && `#${setNumber}`}</span>
            {(cardVariant !== 'Normal' || card?.condition) && (
              <span style={{ fontSize: 11, color: theme.accent }}>
                {cardVariant !== 'Normal' ? cardVariant : ''}{cardVariant !== 'Normal' && card?.condition ? ' - ' : ''}{card?.condition || ''}
              </span>
            )}
          </div>
        </div>

        {/* Refresh Icon */}
        <div style={{ position: 'relative' }}>
          <button
            style={{
              ...styles.refreshIconBtn,
              ...(rawSourcesLoading ? styles.refreshIconBtnSpinning : {}),
            }}
            onClick={() => setShowRefreshPopover(prev => !prev)}
            aria-label="Refresh pricing data"
          >
            <RefreshIcon />
          </button>

          {/* Refresh Popover */}
          {showRefreshPopover && (
            <div style={styles.refreshPopover}>
              <div style={styles.refreshPopoverArrow} />
              <div style={styles.refreshPopoverContent}>
                <span style={styles.refreshPopoverLabel}>Last updated</span>
                <span style={styles.refreshPopoverDate}>
                  {rawSources?.fetchedAt
                    ? new Date(rawSources.fetchedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : cachedAt
                      ? cachedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Never'}
                </span>

                <button
                  style={styles.refreshPopoverBtn}
                  onClick={() => {
                    setShowRefreshPopover(false);
                    loadMultiSource(true);
                  }}
                  disabled={rawSourcesLoading}
                >
                  {rawSourcesLoading ? 'Refreshing...' : 'Refresh Prices'}
                </button>

                <p style={styles.refreshPopoverNote}>
                  Pricing data is saved and reused for fast loading. For cards you're actively buying, selling, or trading, refresh to get the latest sold prices. For most cards, pricing shifts gradually - data older than 60 days is still a solid reference unless it's a chase card or recent release with volatile demand.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expanded card data */}
      <AnimatePresence>
        {cardExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden', borderBottom: `1px solid ${theme.border}` }}
          >
            <div style={{ padding: '12px 16px', display: 'flex', gap: 12, fontSize: 12, color: theme.textMuted, flexWrap: 'wrap' }}>
              {card?.name && card.name !== cardName && (
                <span>Original: <strong style={{ color: theme.text }}>{card.name}</strong></span>
              )}
              {card?.set_name && <span>Set: <strong style={{ color: theme.text }}>{card.set_name}</strong></span>}
              {(card?.set_number || card?.card_number) && <span>No: <strong style={{ color: theme.text }}>{card.set_number || card.card_number}</strong></span>}
              {card?.grade && <span>Grade: <strong style={{ color: theme.text }}>{card.grade}</strong></span>}
              {card?.variant && <span>Variant: <strong style={{ color: theme.text }}>{card.variant}</strong></span>}
              {card?.rarity && <span>Rarity: <strong style={{ color: theme.text }}>{card.rarity}</strong></span>}
              {card?.language && <span>Lang: <strong style={{ color: theme.text }}>{card.language}</strong></span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================ */}
      {/* NO DATA STATE */}
      {/* ============================================ */}
      {phase === 'no_data' && (
        <motion.div
          style={styles.noDataContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <img src={ChefImage} alt="Chef" style={styles.noDataChef} />
          <h2 style={styles.noDataTitle}>No Research Yet</h2>
          <p style={styles.noDataText}>
            I haven't researched this card's pricing yet.
            Click below to start the research!
          </p>
          <div style={{ display: 'flex', gap: 12, flexDirection: 'column', alignItems: 'center' }}>
            <button
              style={styles.researchButton}
              onClick={startResearchV3}
              disabled={isResearching}
            >
              {isResearching ? 'Researching...' : 'Start Research (V3)'}
            </button>
            <p style={{ fontSize: 11, color: '#22c55e', marginTop: 0, opacity: 0.9 }}>
              Countdown API - Direct eBay Sold Data
            </p>
            <button
              style={{ ...styles.researchButton, backgroundColor: '#6b7280', padding: '10px 24px', fontSize: 13 }}
              onClick={startResearchV2}
              disabled={isResearching}
            >
              V2 (Gemini - Backup)
            </button>
          </div>
        </motion.div>
      )}

      {/* ============================================ */}
      {/* RESEARCHING STATE - Can navigate away */}
      {/* ============================================ */}
      {phase === 'researching' && (
        <motion.div
          style={styles.researchingContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.img
            src={ChefImage}
            alt="Chef"
            style={styles.researchingChef}
            animate={{ rotate: [0, -5, 5, -5, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
          <h2 style={styles.researchingTitle}>Research Started!</h2>
          <p style={styles.researchingText}>
            Chef is cooking up the data. This takes about 1-2 minutes.
          </p>
          <p style={styles.researchingSubtext}>
            Feel free to navigate around - come back anytime to check results!
          </p>
          <div style={styles.researchingActions}>
            <button
              style={styles.backToCardButton}
              onClick={() => navigate(-1)}
            >
              Back to Card
            </button>
            <button
              style={styles.goToCollectionButton}
              onClick={() => navigate('/collection')}
            >
              Go to Collection
            </button>
          </div>
          <div style={styles.researchingNote}>
            Results will be saved automatically
          </div>
        </motion.div>
      )}

      {/* ============================================ */}
      {/* DISPLAY PHASE */}
      {/* ============================================ */}
      {phase === 'display' && researchData && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={styles.displayContainer}
        >
          {/* Cache Status / Progress Indicator */}
          <div style={styles.cacheStatus}>
            {isResearching && researchPart === 2 ? (
              <span style={styles.progressText}>
                Part 2/2: BGS, SGC, ACE, TAG, Raw...
              </span>
            ) : (
              <span style={styles.cacheText}>
                {isFresh ? 'Fresh data' : 'Cached'} {cachedAt && `(${Math.floor((Date.now() - cachedAt.getTime()) / (1000 * 60 * 60 * 24))}d ago)`}
              </span>
            )}
            {isDemoMode && <SettingsWheel />}
          </div>

          {/* ============================================ */}
          {/* CHEF'S RESEARCH REPORT */}
          {/* ============================================ */}
          <div style={styles.researchReport}>
            <div style={styles.reportHeader}>
              <img src={ChefImage} alt="Chef" style={styles.reportChef} />
              <div style={styles.reportSpeech}>
                {researchData.evidence && researchData.evidence.length > 0 ? (
                  <>
                    <p style={styles.reportQuote}>
                      "I've pulled the files on this one. Found {researchData.evidence.length.toLocaleString()} verified sales across my records."
                    </p>
                    {(() => {
                      const dateRange = getEvidenceDateRange();
                      if (!dateRange) return null;
                      return (
                        <div style={styles.reportMeta}>
                          <span style={styles.reportDateRange}>
                            Records from {dateRange.oldest} - {dateRange.newest}
                          </span>
                          <button
                            style={styles.infoButton}
                            onClick={() => setShowDataInfo(!showDataInfo)}
                            aria-label="Data coverage info"
                          >
                            <InfoIcon />
                          </button>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <p style={styles.reportQuote}>
                    "Hmm, my files came up empty on this one. No verified sales in my records."
                  </p>
                )}
              </div>
            </div>

            {showDataInfo && (
              <div style={styles.infoTooltip}>
                <p style={styles.infoText}>
                  I prioritize recent market data to give you accurate current pricing.
                  For popular cards, my records may only span a few months due to high transaction volume.
                </p>
                <button
                  style={styles.infoClose}
                  onClick={() => setShowDataInfo(false)}
                >
                  Got it
                </button>
              </div>
            )}

            {/* ROI Calculator */}
            {roi && (
              <div style={styles.roiCard}>
                <span style={styles.roiLabel}>Grading ROI (PSA 10)</span>
                <div style={styles.roiCalc}>
                  <span style={styles.roiRaw}>Raw NM: {formatPrice(roi.rawNM)}</span>
                  <span style={styles.roiArrow}>-&gt;</span>
                  <span style={styles.roiGraded}>PSA 10: {formatPrice(roi.psa10)}</span>
                </div>
                <span style={{
                  ...styles.roiProfit,
                  color: roi.profit > 0 ? '#22c55e' : '#ef4444',
                }}>
                  {roi.profit > 0 ? '+' : ''}{formatPrice(roi.profit)} ({roi.percentage}%)
                </span>
              </div>
            )}
          </div>

          {/* ============================================ */}
          {/* ZONE A: Condition Switcher */}
          {/* ============================================ */}
          <div style={styles.zoneSwitcher}>
            <button
              style={{
                ...styles.switcherTab,
                ...(viewMode === 'raw' ? styles.switcherTabActive : {}),
              }}
              onClick={() => setViewMode('raw')}
            >
              Raw
            </button>
            <button
              style={{
                ...styles.switcherTab,
                ...(viewMode === 'graded' ? styles.switcherTabActive : {}),
              }}
              onClick={() => setViewMode('graded')}
            >
              Graded
            </button>
          </div>

          {/* ============================================ */}
          {/* RAW VIEW - Multi-Source Tabs */}
          {/* ============================================ */}
          {viewMode === 'raw' && (
            <motion.div
              style={styles.rawContainer}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Sub-tab bar */}
              <div style={styles.rawSubTabs}>
                {[
                  { key: 'summary', label: 'Summary' },
                  { key: 'gemini', label: 'Gemini' },
                  { key: 'tcgplayer', label: 'TCGPlayer' },
                  { key: 'cardmarket', label: 'CardMarket' },
                  { key: 'justtcg', label: 'JustTCG' },
                  { key: 'poketrace', label: 'Poketrace' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    style={{
                      ...styles.rawSubTab,
                      ...(rawSubTab === tab.key ? styles.rawSubTabActive : {}),
                    }}
                    onClick={() => {
                      setRawSubTab(tab.key);
                      if (tab.key !== 'poketrace' && tab.key !== 'summary' && !rawSources) {
                        loadMultiSource();
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* POKETRACE sub-tab (always available from existing data) */}
              {rawSubTab === 'poketrace' && (
                <div>
                  <h3 style={styles.sectionTitle}>Condition Spectrum</h3>
                  <div style={styles.conditionBar}>
                    {RAW_CONDITIONS.map((cond, idx) => {
                      const ptData = rawSources?.poketrace?.raw?.[cond.id];
                      const price = ptData?.avg ?? getRawPrice(cond.id);
                      const hasPrice = price !== null && price !== undefined;
                      return (
                        <div
                          key={cond.id}
                          style={{
                            ...styles.conditionItem,
                            backgroundColor: hasPrice
                              ? `rgba(34, 197, 94, ${0.7 - (idx * 0.15)})`
                              : 'rgba(100, 100, 100, 0.1)',
                          }}
                        >
                          <span style={styles.conditionLabel}>{cond.label}</span>
                          <div style={{ flex: 1, textAlign: 'right', marginRight: 12 }}>
                            <span style={{
                              ...styles.conditionPrice,
                              flex: 'none',
                              textAlign: 'right',
                              marginRight: 0,
                              color: hasPrice ? '#22c55e' : theme.textMuted,
                            }}>
                              {formatPrice(price)}
                            </span>
                            {ptData && ptData.low != null && ptData.high != null && (
                              <span style={{ fontSize: 10, color: theme.textMuted, display: 'block' }}>
                                {formatPriceDetail(ptData.low)} - {formatPriceDetail(ptData.high)}
                              </span>
                            )}
                          </div>
                          <div style={{ width: 100 }}>
                            <span style={styles.conditionFull}>{cond.full}</span>
                            {ptData?.saleCount > 0 && (
                              <span style={{ fontSize: 10, color: theme.textMuted, display: 'block' }}>
                                {ptData.saleCount} sales
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TCGPLAYER sub-tab */}
              {rawSubTab === 'tcgplayer' && (
                <div>
                  {rawSourcesLoading && (
                    <div style={styles.rawSourceLoading}>
                      <div style={styles.rawSourceSpinner} />
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Loading TCGPlayer prices...</span>
                    </div>
                  )}
                  {!rawSourcesLoading && !rawSources?.tcgdex?.tcgplayer && (
                    <div style={styles.rawSourceEmpty}>
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>No TCGPlayer pricing available for this card</span>
                    </div>
                  )}
                  {rawSources?.tcgdex?.tcgplayer && (
                    <div>
                      <h3 style={styles.sectionTitle}>TCGPlayer Prices</h3>
                      <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12, marginTop: 0 }}>
                        Live market listing prices (USD)
                      </p>
                      <div style={{ ...styles.rawSourceCard, border: `1px solid ${theme.border}`, backgroundColor: theme.bgCard }}>
                        {['normal', 'holofoil', 'reverseHolofoil', 'firstEdition'].map(variant => {
                          const v = rawSources.tcgdex.tcgplayer[variant];
                          if (!v) return null;
                          const label = variant === 'reverseHolofoil' ? 'Reverse Holo' :
                            variant === 'firstEdition' ? '1st Edition' :
                            variant.charAt(0).toUpperCase() + variant.slice(1);
                          return (
                            <div key={variant} style={styles.rawSourceRow}>
                              <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>{label}</span>
                              <div style={styles.rawSourcePrices}>
                                {v.low != null && <span style={styles.rawSourcePriceItem}>Low: <b style={{ color: '#22c55e' }}>${v.low.toFixed(2)}</b></span>}
                                {v.mid != null && <span style={styles.rawSourcePriceItem}>Mid: <b style={{ color: '#22c55e' }}>${v.mid.toFixed(2)}</b></span>}
                                {v.market != null && <span style={styles.rawSourcePriceItem}>Market: <b style={{ color: '#22c55e' }}>${v.market.toFixed(2)}</b></span>}
                                {v.high != null && <span style={styles.rawSourcePriceItem}>High: <b style={{ color: '#22c55e' }}>${v.high.toFixed(2)}</b></span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CARDMARKET sub-tab */}
              {rawSubTab === 'cardmarket' && (
                <div>
                  {rawSourcesLoading && (
                    <div style={styles.rawSourceLoading}>
                      <div style={styles.rawSourceSpinner} />
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Loading CardMarket prices...</span>
                    </div>
                  )}
                  {!rawSourcesLoading && !rawSources?.tcgdex?.cardmarket && (
                    <div style={styles.rawSourceEmpty}>
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>No CardMarket pricing available for this card</span>
                    </div>
                  )}
                  {rawSources?.tcgdex?.cardmarket && (
                    <div>
                      <h3 style={styles.sectionTitle}>CardMarket Prices</h3>
                      <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12, marginTop: 0 }}>
                        European market prices (EUR with USD conversion)
                      </p>
                      <div style={{ ...styles.rawSourceCard, border: `1px solid ${theme.border}`, backgroundColor: theme.bgCard }}>
                        {(() => {
                          const cm = rawSources.tcgdex.cardmarket;
                          return (
                            <>
                              <div style={styles.rawSourceRow}>
                                <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>Average</span>
                                <div style={styles.rawSourcePrices}>
                                  {cm.avgUSD != null && <span style={styles.rawSourcePriceItem}><b style={{ color: '#22c55e' }}>${cm.avgUSD.toFixed(2)}</b> <span style={{ color: theme.textMuted, fontSize: 10 }}>({'\u20AC'}{cm.avg?.toFixed(2)})</span></span>}
                                </div>
                              </div>
                              <div style={styles.rawSourceRow}>
                                <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>Low</span>
                                <div style={styles.rawSourcePrices}>
                                  {cm.lowUSD != null && <span style={styles.rawSourcePriceItem}><b style={{ color: '#22c55e' }}>${cm.lowUSD.toFixed(2)}</b></span>}
                                </div>
                              </div>
                              <div style={styles.rawSourceRow}>
                                <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>Trend</span>
                                <div style={styles.rawSourcePrices}>
                                  {cm.trendUSD != null && <span style={styles.rawSourcePriceItem}><b style={{ color: '#22c55e' }}>${cm.trendUSD.toFixed(2)}</b></span>}
                                </div>
                              </div>
                              {cm.avg7 != null && (
                                <div style={styles.rawSourceRow}>
                                  <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>7d Avg</span>
                                  <div style={styles.rawSourcePrices}>
                                    <span style={styles.rawSourcePriceItem}><b style={{ color: theme.textSecondary }}>{'\u20AC'}{cm.avg7.toFixed(2)}</b></span>
                                  </div>
                                </div>
                              )}
                              {cm.avg30 != null && (
                                <div style={styles.rawSourceRow}>
                                  <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>30d Avg</span>
                                  <div style={styles.rawSourcePrices}>
                                    <span style={styles.rawSourcePriceItem}><b style={{ color: theme.textSecondary }}>{'\u20AC'}{cm.avg30.toFixed(2)}</b></span>
                                  </div>
                                </div>
                              )}
                              {cm.holoAvgUSD != null && (
                                <>
                                  <div style={{ ...styles.rawSourceCardHeader, marginTop: 8 }}>
                                    <span style={{ fontWeight: 600, fontSize: 12, color: theme.textSecondary }}>Holo Variant</span>
                                  </div>
                                  <div style={styles.rawSourceRow}>
                                    <span style={{ fontSize: 12, color: theme.textSecondary, width: 90 }}>Holo Avg</span>
                                    <div style={styles.rawSourcePrices}>
                                      <span style={styles.rawSourcePriceItem}><b style={{ color: '#22c55e' }}>${cm.holoAvgUSD.toFixed(2)}</b></span>
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* JUSTTCG sub-tab */}
              {rawSubTab === 'justtcg' && (
                <div>
                  {rawSourcesLoading && (
                    <div style={styles.rawSourceLoading}>
                      <div style={styles.rawSourceSpinner} />
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Loading JustTCG prices...</span>
                    </div>
                  )}
                  {!rawSourcesLoading && !rawSources?.justTCG && (
                    <div style={styles.rawSourceEmpty}>
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>No JustTCG pricing available for this card</span>
                    </div>
                  )}
                  {rawSources?.justTCG && (
                    <div>
                      <h3 style={styles.sectionTitle}>JustTCG Per-Condition Prices</h3>
                      <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12, marginTop: 0 }}>
                        TCGPlayer marketplace prices by condition{rawSources.justTCG.variant ? ` (${rawSources.justTCG.variant})` : ''}
                      </p>
                      <div style={styles.conditionBar}>
                        {RAW_CONDITIONS.map((cond, idx) => {
                          const jData = rawSources.justTCG.conditions?.[cond.id];
                          const hasPrice = jData?.price != null;
                          return (
                            <div
                              key={cond.id}
                              style={{
                                ...styles.conditionItem,
                                backgroundColor: hasPrice
                                  ? `rgba(59, 130, 246, ${0.6 - (idx * 0.12)})`
                                  : 'rgba(100, 100, 100, 0.1)',
                              }}
                            >
                              <span style={styles.conditionLabel}>{cond.label}</span>
                              <div style={{ flex: 1, textAlign: 'right', marginRight: 12 }}>
                                <span style={{
                                  ...styles.conditionPrice,
                                  flex: 'none',
                                  textAlign: 'right',
                                  marginRight: 0,
                                  color: hasPrice ? '#3b82f6' : theme.textMuted,
                                }}>
                                  {hasPrice ? `$${jData.price.toFixed(2)}` : '--'}
                                </span>
                                {jData?.change7d != null && (
                                  <span style={{
                                    fontSize: 10,
                                    color: jData.change7d >= 0 ? '#22c55e' : '#ef4444',
                                    display: 'block',
                                  }}>
                                    7d: {jData.change7d >= 0 ? '+' : ''}{jData.change7d.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                              <div style={{ width: 100 }}>
                                <span style={styles.conditionFull}>{cond.full}</span>
                                {jData?.change30d != null && (
                                  <span style={{
                                    fontSize: 10,
                                    color: jData.change30d >= 0 ? '#22c55e' : '#ef4444',
                                    display: 'block',
                                  }}>
                                    30d: {jData.change30d >= 0 ? '+' : ''}{jData.change30d.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {rawSources.justTCG.allVariants?.length > 1 && (
                        <div style={{ marginTop: 12 }}>
                          <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6, marginTop: 0 }}>
                            All available variants:
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {rawSources.justTCG.allVariants.map(v => (
                              <span key={v} style={{
                                fontSize: 11,
                                padding: '3px 8px',
                                borderRadius: 4,
                                backgroundColor: v === rawSources.justTCG.variant
                                  ? 'rgba(59, 130, 246, 0.2)'
                                  : `rgba(100, 100, 100, 0.1)`,
                                border: v === rawSources.justTCG.variant
                                  ? '1px solid rgba(59, 130, 246, 0.4)'
                                  : `1px solid ${theme.border}`,
                                color: v === rawSources.justTCG.variant ? '#3b82f6' : theme.textMuted,
                                fontWeight: v === rawSources.justTCG.variant ? 600 : 400,
                              }}>
                                {v}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* GEMINI sub-tab */}
              {rawSubTab === 'gemini' && (
                <div>
                  {rawSourcesLoading && (
                    <div style={styles.rawSourceLoading}>
                      <div style={styles.rawSourceSpinner} />
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Analyzing with Gemini AI...</span>
                    </div>
                  )}
                  {!rawSourcesLoading && !rawSources?.gemini && (
                    <div style={styles.rawSourceEmpty}>
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Gemini analysis unavailable</span>
                      <button
                        style={{ ...styles.rawRetryBtn, border: `1px solid ${theme.border}`, color: theme.accent }}
                        onClick={loadMultiSource}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {rawSources?.gemini && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>AI Price Analysis</h3>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          padding: '3px 8px',
                          borderRadius: 4,
                          backgroundColor: rawSources.gemini.confidence === 'high' ? 'rgba(34, 197, 94, 0.15)' :
                            rawSources.gemini.confidence === 'medium' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          color: rawSources.gemini.confidence === 'high' ? '#22c55e' :
                            rawSources.gemini.confidence === 'medium' ? '#eab308' : '#ef4444',
                        }}>
                          {rawSources.gemini.confidence} confidence
                        </span>
                      </div>
                      <div style={styles.conditionBar}>
                        {RAW_CONDITIONS.map((cond, idx) => {
                          const gData = rawSources.gemini.raw?.[cond.id];
                          const price = gData?.price;
                          const hasPrice = price != null;
                          return (
                            <div
                              key={cond.id}
                              style={{
                                ...styles.conditionItem,
                                backgroundColor: hasPrice
                                  ? `rgba(139, 92, 246, ${0.5 - (idx * 0.1)})`
                                  : 'rgba(100, 100, 100, 0.1)',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 4,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={styles.conditionLabel}>{cond.label}</span>
                                <span style={{
                                  ...styles.conditionPrice,
                                  color: hasPrice ? '#a78bfa' : theme.textMuted,
                                }}>
                                  {hasPrice ? `$${price.toFixed(2)}` : '--'}
                                </span>
                                <span style={styles.conditionFull}>{cond.full}</span>
                              </div>
                              {gData?.reasoning && (
                                <span style={{ fontSize: 11, color: theme.textMuted, paddingLeft: 40 }}>
                                  {gData.reasoning}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SUMMARY sub-tab */}
              {rawSubTab === 'summary' && (
                <div>
                  {rawSourcesLoading && (
                    <div style={styles.rawSourceLoading}>
                      <div style={styles.rawSourceSpinner} />
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Loading comparison...</span>
                    </div>
                  )}
                  {!rawSourcesLoading && !rawSources && (
                    <div style={styles.rawSourceEmpty}>
                      <span style={{ color: theme.textMuted, fontSize: 13 }}>Multi-source data not loaded yet</span>
                      <button
                        style={{ ...styles.rawRetryBtn, border: `1px solid ${theme.border}`, color: theme.accent }}
                        onClick={loadMultiSource}
                      >
                        Load Sources
                      </button>
                    </div>
                  )}
                  {rawSources && (
                    <div>
                      <h3 style={styles.sectionTitle}>Source Comparison</h3>

                      {/* Comparison table */}
                      <div style={{ ...styles.rawSourceCard, border: `1px solid ${theme.border}`, backgroundColor: theme.bgCard, overflow: 'hidden' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', padding: '10px 12px', borderBottom: `1px solid ${theme.border}` }}>
                          <span style={{ width: 40, fontSize: 10, fontWeight: 700, color: theme.textMuted }}>Cond</span>
                          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: theme.textMuted, textAlign: 'right' }}>Poketrace</span>
                          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: theme.textMuted, textAlign: 'right' }}>Market</span>
                          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: theme.textMuted, textAlign: 'right' }}>JustTCG</span>
                          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: theme.textMuted, textAlign: 'right' }}>Gemini</span>
                        </div>
                        {/* Rows */}
                        {RAW_CONDITIONS.map((cond) => {
                          const ptPrice = rawSources.poketrace?.raw?.[cond.id]?.avg ?? getRawPrice(cond.id);
                          const tcgBest = cond.id === 'nm' ? (() => {
                            const tcg = rawSources.tcgdex?.tcgplayer;
                            const cm = rawSources.tcgdex?.cardmarket;
                            const v = tcg?.holofoil || tcg?.normal;
                            return v?.market || cm?.avgUSD || null;
                          })() : null;
                          const jtPrice = rawSources.justTCG?.conditions?.[cond.id]?.price ?? null;
                          const gmPrice = rawSources.gemini?.raw?.[cond.id]?.high || rawSources.gemini?.raw?.[cond.id]?.price;
                          return (
                            <div key={cond.id} style={{ display: 'flex', padding: '8px 12px', borderBottom: `1px solid ${theme.border}` }}>
                              <span style={{ width: 40, fontSize: 12, fontWeight: 600, color: theme.text }}>{cond.label}</span>
                              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: ptPrice != null ? '#22c55e' : theme.textMuted, textAlign: 'right' }}>
                                {ptPrice != null ? `$${Number(ptPrice).toFixed(2)}` : '--'}
                              </span>
                              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: tcgBest != null ? '#3b82f6' : theme.textMuted, textAlign: 'right' }}>
                                {tcgBest != null ? `$${Number(tcgBest).toFixed(2)}` : '--'}
                              </span>
                              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: jtPrice != null ? '#3b82f6' : theme.textMuted, textAlign: 'right' }}>
                                {jtPrice != null ? `$${Number(jtPrice).toFixed(2)}` : '--'}
                              </span>
                              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: gmPrice != null ? '#a78bfa' : theme.textMuted, textAlign: 'right' }}>
                                {gmPrice != null ? `$${Number(gmPrice).toFixed(2)}` : '--'}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Best price */}
                      {rawSources.bestPrice && (
                        <div style={{ ...styles.rawSourceCard, border: '1px solid #22c55e', backgroundColor: 'rgba(34, 197, 94, 0.08)', marginTop: 10, padding: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase' }}>Best Price</span>
                            <span style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>
                              {rawSources.bestPrice.isRange && rawSources.bestPrice.priceLow
                                ? `$${rawSources.bestPrice.priceLow.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - $${rawSources.bestPrice.priceHigh.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : `$${rawSources.bestPrice.price.toFixed(2)}`
                              }
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, display: 'block' }}>
                            {rawSources.bestPrice.reasoning} ({rawSources.bestPrice.source})
                          </span>
                        </div>
                      )}

                      {/* Gemini market intelligence */}
                      {rawSources.gemini?.summary && (
                        <div style={{ ...styles.rawSourceCard, border: `1px solid ${theme.border}`, backgroundColor: theme.bgCard, marginTop: 10, padding: 16 }}>
                          <h3 style={{ ...styles.sectionTitle, marginBottom: 10 }}>Market Intelligence</h3>
                          {rawSources.gemini.usedGrounding && (
                            <div style={{
                              padding: '8px 12px',
                              borderRadius: 8,
                              backgroundColor: 'rgba(234, 179, 8, 0.1)',
                              border: '1px solid rgba(234, 179, 8, 0.3)',
                              marginBottom: 10,
                            }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: '#eab308' }}>
                                Web search was used -- automated data was flagged as unreliable
                              </span>
                              {rawSources.gemini.groundingReason && (
                                <span style={{ fontSize: 11, color: theme.textMuted, display: 'block', marginTop: 4 }}>
                                  {rawSources.gemini.groundingReason}
                                </span>
                              )}
                            </div>
                          )}
                          <div style={{ fontSize: 13, lineHeight: 1.7, color: theme.textSecondary }}>
                            {rawSources.gemini.summary.split('. ').reduce((acc, sentence, i, arr) => {
                              // Group sentences into paragraphs of 2-3 for readability
                              if (i % 2 === 0) {
                                const chunk = arr.slice(i, i + 2).join('. ');
                                if (chunk.trim()) {
                                  acc.push(
                                    <p key={i} style={{ margin: '0 0 10px 0' }}>
                                      {chunk.endsWith('.') ? chunk : chunk + '.'}
                                    </p>
                                  );
                                }
                              }
                              return acc;
                            }, [])}
                          </div>
                          {rawSources.gemini.groundingMetadata?.sources?.length > 0 && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${theme.border}` }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>Sources</span>
                              {rawSources.gemini.groundingMetadata.sources.map((src, idx) => (
                                <a
                                  key={idx}
                                  href={src.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: theme.accent, display: 'block', marginTop: 4, textDecoration: 'none' }}
                                >
                                  {src.title || src.url}
                                </a>
                              ))}
                            </div>
                          )}
                          {rawSources.gemini.confidence && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                              <span style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                padding: '3px 8px',
                                borderRadius: 4,
                                backgroundColor: rawSources.gemini.confidence === 'high' ? 'rgba(34, 197, 94, 0.15)' :
                                  rawSources.gemini.confidence === 'medium' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                color: rawSources.gemini.confidence === 'high' ? '#22c55e' :
                                  rawSources.gemini.confidence === 'medium' ? '#eab308' : '#ef4444',
                              }}>
                                {rawSources.gemini.confidence} confidence
                              </span>
                              <span style={{ fontSize: 10, color: theme.textMuted }}>
                                Gemini 2.5 Flash{rawSources.gemini.usedGrounding ? ' + Google Search' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* ============================================ */}
          {/* GRADED VIEW - Company Tabs + Price Matrix */}
          {/* ============================================ */}
          {viewMode === 'graded' && (
            <motion.div
              style={styles.gradedContainer}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Company Tabs */}
              <div style={styles.companyTabs}>
                {COMPANIES.map(company => (
                  <button
                    key={company}
                    style={{
                      ...styles.companyTab,
                      ...(selectedCompany === company ? styles.companyTabActive : {}),
                    }}
                    onClick={() => {
                      setSelectedCompany(company);
                      setExpandedGrade(null);
                    }}
                  >
                    {COMPANY_LABELS[company]}
                  </button>
                ))}

                {/* Compare Button - Demo Mode Only */}
                {isDemoMode && (
                  <button
                    style={styles.compareButton}
                    onClick={() => navigate('/chefs-lab/compare')}
                  >
                    Compare
                  </button>
                )}
              </div>

              {/* ZONE B: Price Matrix - Multi-Period Columns */}
              <div style={styles.priceMatrix}>
                {/* Header Row */}
                <div style={styles.priceMatrixHeader}>
                  <span style={styles.headerGrade}>Grade</span>
                  <span style={styles.headerCount}>#</span>
                  <span
                    style={{
                      ...styles.headerPeriod,
                      ...(isDemoMode && preferences?.selectedTimePeriod === 'all' ? styles.headerPeriodActive : {}),
                      cursor: isDemoMode ? 'pointer' : 'default',
                    }}
                    onClick={() => isDemoMode && setContextTimePeriod('all')}
                  >
                    All
                  </span>
                  <span
                    style={{
                      ...styles.headerPeriod,
                      ...(isDemoMode && preferences?.selectedTimePeriod === '12m' ? styles.headerPeriodActive : {}),
                      cursor: isDemoMode ? 'pointer' : 'default',
                    }}
                    onClick={() => isDemoMode && setContextTimePeriod('12m')}
                  >
                    12mo
                  </span>
                  <span
                    style={{
                      ...styles.headerPeriod,
                      ...(isDemoMode && preferences?.selectedTimePeriod === '6m' ? styles.headerPeriodActive : {}),
                      cursor: isDemoMode ? 'pointer' : 'default',
                    }}
                    onClick={() => isDemoMode && setContextTimePeriod('6m')}
                  >
                    6mo
                  </span>
                  <span
                    style={{
                      ...styles.headerPeriod,
                      ...(isDemoMode && preferences?.selectedTimePeriod === '3m' ? styles.headerPeriodActive : {}),
                      cursor: isDemoMode ? 'pointer' : 'default',
                    }}
                    onClick={() => isDemoMode && setContextTimePeriod('3m')}
                  >
                    3mo
                  </span>
                  <span style={{ width: 16 }} /> {/* Spacer for expand icon alignment */}
                </div>

                {GRADES.map(grade => {
                  const averages = getAllPeriodAverages(selectedCompany, grade);
                  // In demo mode, use filtered count based on preferences
                  const filteredListings = isDemoMode && contextGetDisplayListings
                    ? contextGetDisplayListings(averages.listings)
                    : averages.listings;
                  const displayCount = filteredListings.length;
                  const hasData = displayCount > 0;
                  const isExpanded = expandedGrade === grade;
                  const isHighest = highestValue &&
                    highestValue.company === selectedCompany &&
                    highestValue.grade === grade;

                  return (
                    <div key={grade}>
                      {/* Grade Row - Tappable */}
                      <div
                        style={{
                          ...styles.gradeRow,
                          ...(isHighest ? styles.gradeRowHighest : {}),
                          ...(isExpanded ? styles.gradeRowExpanded : {}),
                          cursor: hasData ? 'pointer' : 'default',
                        }}
                        onClick={() => hasData && setExpandedGrade(isExpanded ? null : grade)}
                      >
                        <span style={styles.gradeLabel}>{grade}</span>
                        {hasData ? (
                          <>
                            <span style={styles.listingCount}>{displayCount}</span>
                            <span style={{
                              ...styles.periodPrice,
                              color: averages.all
                                ? (isDemoMode && preferences?.selectedTimePeriod === 'all' ? '#3b82f6' : '#22c55e')
                                : theme.textMuted,
                              fontWeight: isDemoMode && preferences?.selectedTimePeriod === 'all' && averages.all ? '600' : '400',
                            }}>
                              {averages.all ? formatPrice(averages.all) : '--'}
                            </span>
                            <span style={{
                              ...styles.periodPrice,
                              color: averages['12m']
                                ? (isDemoMode && preferences?.selectedTimePeriod === '12m' ? '#3b82f6' : '#22c55e')
                                : theme.textMuted,
                              fontWeight: isDemoMode && preferences?.selectedTimePeriod === '12m' && averages['12m'] ? '600' : '400',
                            }}>
                              {averages['12m'] ? formatPrice(averages['12m']) : '--'}
                            </span>
                            <span style={{
                              ...styles.periodPrice,
                              color: averages['6m']
                                ? (isDemoMode && preferences?.selectedTimePeriod === '6m' ? '#3b82f6' : '#22c55e')
                                : theme.textMuted,
                              fontWeight: isDemoMode && preferences?.selectedTimePeriod === '6m' && averages['6m'] ? '600' : '400',
                            }}>
                              {averages['6m'] ? formatPrice(averages['6m']) : '--'}
                            </span>
                            <span style={{
                              ...styles.periodPrice,
                              color: averages['3m']
                                ? (isDemoMode && preferences?.selectedTimePeriod === '3m' ? '#3b82f6' : '#22c55e')
                                : theme.textMuted,
                              fontWeight: isDemoMode && preferences?.selectedTimePeriod === '3m' && averages['3m'] ? '600' : '400',
                            }}>
                              {averages['3m'] ? formatPrice(averages['3m']) : '--'}
                            </span>
                            <span style={{
                              ...styles.expandIcon,
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}>
                              <ChevronIcon />
                            </span>
                          </>
                        ) : (
                          <span style={styles.noSoldHistory}>No sold history</span>
                        )}
                      </div>

                      {/* Expanded Listings */}
                      {isExpanded && (
                        <motion.div
                          style={styles.expandedListings}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                        >
                          {isDemoMode ? (
                            <>
                              {/* Demo mode: Show listings filtered by preferences */}
                              {(() => {
                                const displayListings = contextGetDisplayListings
                                  ? contextGetDisplayListings(averages.listings)
                                  : averages.listings;
                                return displayListings
                                  .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                                  .map((listing, idx) => (
                                    <ListingCard
                                      key={idx}
                                      listing={listing}
                                      onReport={(l) => {
                                        setListingToReport(l);
                                        setReportModalOpen(true);
                                      }}
                                    />
                                  ));
                              })()}
                              {((contextGetDisplayListings ? contextGetDisplayListings(averages.listings) : averages.listings).length === 0) && (
                                <div style={{ padding: '20px', textAlign: 'center', color: theme.textMuted }}>
                                  No listings for this grade
                                </div>
                              )}
                            </>
                          ) : (
                            /* Standard mode: Simple listing view */
                            averages.listings
                              .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                              .map((listing, idx) => (
                                <a
                                  key={idx}
                                  href={listing.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={styles.expandedListingItem}
                                >
                                  <span style={styles.expandedListingPrice}>
                                    {formatPrice(listing.price)}
                                  </span>
                                  <span style={styles.expandedListingDate}>
                                    {formatDate(listing.date)}
                                  </span>
                                  <span style={styles.expandedListingSource}>
                                    {getDomain(listing.url)}
                                  </span>
                                  <ExternalLinkIcon />
                                </a>
                              ))
                          )}
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Total listings count */}
              <div style={styles.totalListings}>
                Total: {researchData.evidence?.filter(e =>
                  (e.company?.toLowerCase() === selectedCompany) ||
                  e.grade?.toUpperCase().includes(selectedCompany.toUpperCase())
                ).length || 0} sold listings for {COMPANY_LABELS[selectedCompany]}
              </div>
            </motion.div>
          )}

        </motion.div>
      )}

      {/* Report Modal */}
      {isDemoMode && (
        <ReportModal
          isOpen={reportModalOpen}
          onClose={() => {
            setReportModalOpen(false);
            setListingToReport(null);
          }}
          listing={listingToReport}
        />
      )}
    </div>
  );
};

// ============================================
// STYLES
// ============================================

const getStyles = (theme) => ({
  container: {
    minHeight: '100vh',
    backgroundColor: theme.bg,
    color: theme.text,
    paddingBottom: 'calc(100px + env(safe-area-inset-bottom))',
  },

  // Loading
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    padding: 24,
  },
  loadingChef: {
    width: 100,
    height: 'auto',
  },
  loadingTextBox: {
    backgroundColor: 'white',
    border: '3px solid #374151',
    borderRadius: 8,
    padding: '14px 20px',
    minWidth: 260,
    textAlign: 'center',
  },
  loadingText: {
    fontFamily: "'PokeText', monospace",
    fontSize: 14,
    color: '#1F2937',
    margin: 0,
  },
  loadingBar: {
    width: 200,
    height: 6,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  loadingBarFill: {
    height: '100%',
    backgroundColor: '#22c55e',
    borderRadius: 3,
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.border}`,
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: theme.text,
    padding: 8,
    cursor: 'pointer',
  },
  headerCard: {
    width: 45,
    height: 63,
    objectFit: 'cover',
    borderRadius: 4,
    transition: 'all 0.2s ease',
  },
  headerCardExpanded: {
    width: 90,
    height: 126,
    objectFit: 'cover',
    borderRadius: 6,
    transition: 'all 0.2s ease',
  },
  headerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  headerName: {
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSet: {
    fontSize: 11,
    color: theme.textMuted,
  },

  // No Data
  noDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    textAlign: 'center',
    minHeight: '60vh',
  },
  noDataChef: {
    width: 80,
    height: 'auto',
    marginBottom: 16,
    opacity: 0.8,
  },
  noDataTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: '0 0 8px 0',
  },
  noDataText: {
    fontSize: 14,
    color: theme.textMuted,
    margin: '0 0 24px 0',
    maxWidth: 280,
  },
  researchButton: {
    padding: '14px 32px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Researching State
  researchingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    textAlign: 'center',
    minHeight: '60vh',
  },
  researchingChef: {
    width: 100,
    height: 'auto',
    marginBottom: 20,
  },
  researchingTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: '0 0 12px 0',
    color: '#22c55e',
  },
  researchingText: {
    fontSize: 15,
    color: theme.text,
    margin: '0 0 8px 0',
    maxWidth: 300,
  },
  researchingSubtext: {
    fontSize: 13,
    color: theme.textMuted,
    margin: '0 0 24px 0',
    maxWidth: 280,
  },
  researchingActions: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  backToCardButton: {
    padding: '12px 20px',
    backgroundColor: 'transparent',
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  goToCollectionButton: {
    padding: '12px 20px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  researchingNote: {
    fontSize: 11,
    color: theme.textMuted,
    fontStyle: 'italic',
  },

  // Display
  displayContainer: {
    padding: 16,
  },

  // Cache Status
  cacheStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  cacheText: {
    fontSize: 12,
    color: theme.textMuted,
  },
  progressText: {
    fontSize: 12,
    color: '#8b5cf6',
    fontWeight: 500,
  },
  refreshButton: {
    background: 'none',
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    padding: 6,
    color: theme.textMuted,
    cursor: 'pointer',
  },
  refreshIconBtn: {
    background: 'none',
    border: 'none',
    color: theme.textMuted,
    padding: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    transition: 'color 0.2s ease',
  },
  refreshIconBtnSpinning: {
    color: '#22c55e',
    animation: 'spin 1s linear infinite',
  },
  refreshPopover: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    zIndex: 50,
    minWidth: 260,
  },
  refreshPopoverArrow: {
    position: 'absolute',
    top: -6,
    right: 14,
    width: 12,
    height: 12,
    backgroundColor: theme.bgCard,
    borderTop: `1px solid ${theme.border}`,
    borderLeft: `1px solid ${theme.border}`,
    borderRight: 'none',
    borderBottom: 'none',
    transform: 'rotate(45deg)',
  },
  refreshPopoverContent: {
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  refreshPopoverLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  refreshPopoverDate: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.text,
  },
  refreshPopoverBtn: {
    padding: '10px 16px',
    backgroundColor: '#22c55e',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  refreshPopoverNote: {
    fontSize: 11,
    lineHeight: 1.5,
    color: theme.textMuted,
    margin: 0,
    marginTop: 4,
    fontStyle: 'italic',
  },

  // Zone Switcher
  zoneSwitcher: {
    display: 'flex',
    backgroundColor: theme.bgCard,
    borderRadius: 10,
    padding: 4,
    marginBottom: 16,
  },
  switcherTab: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    color: theme.textMuted,
    cursor: 'pointer',
  },
  switcherTabActive: {
    backgroundColor: theme.bg,
    color: theme.text,
  },

  // Raw View
  rawContainer: {
    marginBottom: 24,
  },
  rawSubTabs: {
    display: 'flex',
    gap: 6,
    marginBottom: 16,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  rawSubTab: {
    padding: '8px 14px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    backgroundColor: 'transparent',
    color: theme.textMuted,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  rawSubTabActive: {
    backgroundColor: '#22c55e',
    border: '1px solid #22c55e',
    color: '#fff',
  },
  rawSourceLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '32px 0',
  },
  rawSourceSpinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: `3px solid ${theme.border}`,
    borderTop: '3px solid #22c55e',
    animation: 'spin 0.8s linear infinite',
  },
  rawSourceEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '32px 0',
  },
  rawRetryBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  rawSourceCard: {
    border: '1px solid transparent',
    borderRadius: 10,
    overflow: 'hidden',
  },
  rawSourceCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
  },
  rawSourceRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
  },
  rawSourcePrices: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 12px',
    justifyContent: 'flex-end',
  },
  rawSourcePriceItem: {
    fontSize: 12,
    color: theme.textSecondary,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.textMuted,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  conditionBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  conditionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: 10,
  },
  conditionLabel: {
    fontSize: 14,
    fontWeight: 700,
    width: 40,
  },
  conditionPrice: {
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
    textAlign: 'right',
    marginRight: 12,
  },
  conditionFull: {
    fontSize: 11,
    color: theme.textMuted,
    width: 100,
  },

  // Graded View
  gradedContainer: {
    marginBottom: 24,
  },
  timePeriodToggle: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  timePeriodBtn: {
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${theme.border}`,
    backgroundColor: 'transparent',
    color: theme.textMuted,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  timePeriodBtnActive: {
    backgroundColor: '#8b5cf6',
    border: '1px solid #8b5cf6',
    color: '#fff',
  },
  companyTabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    overflowX: 'auto',
  },
  companyTab: {
    padding: '10px 16px',
    borderRadius: 20,
    border: `1px solid ${theme.border}`,
    backgroundColor: 'transparent',
    color: theme.textMuted,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  companyTabActive: {
    backgroundColor: '#0ea5e9',
    border: '1px solid #0ea5e9',
    color: '#fff',
  },
  compareButton: {
    padding: '10px 16px',
    borderRadius: 20,
    border: `1px solid ${theme.accent}`,
    backgroundColor: 'transparent',
    color: theme.accent,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },

  // Price Matrix
  priceMatrix: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  priceMatrixHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    gap: 4,
    borderBottom: `1px solid ${theme.border}`,
    marginBottom: 4,
  },
  headerGrade: {
    width: 36,
    fontSize: 10,
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  headerPeriod: {
    flex: 1,
    fontSize: 10,
    fontWeight: 600,
    color: theme.textMuted,
    textAlign: 'center',
    textTransform: 'uppercase',
    transition: 'all 0.2s ease',
    padding: '4px 2px',
    borderRadius: 6,
  },
  headerPeriodActive: {
    color: '#fff',
    fontWeight: 700,
    backgroundColor: '#3b82f6',
  },
  headerCount: {
    width: 24,
    fontSize: 10,
    fontWeight: 600,
    color: theme.textMuted,
    textAlign: 'center',
  },
  gradeRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    gap: 4,
    backgroundColor: theme.bgCard,
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
  },
  periodPrice: {
    flex: 1,
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'monospace',
  },
  gradeRowHighest: {
    border: '2px solid #fbbf24',
    backgroundColor: 'rgba(251, 191, 36, 0.1)',
  },
  gradeLabel: {
    fontSize: 15,
    fontWeight: 700,
    width: 36,
    textAlign: 'center',
  },
  gradePrice: {
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
    textAlign: 'right',
    marginRight: 12,
  },
  evidenceLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    textDecoration: 'none',
  },
  listingCount: {
    fontSize: 11,
    color: theme.textMuted,
    width: 24,
    textAlign: 'center',
  },
  noSoldHistory: {
    flex: 1,
    fontSize: 12,
    color: theme.textMuted,
    fontStyle: 'italic',
    textAlign: 'center',
    opacity: 0.6,
  },
  expandIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    color: theme.textMuted,
    transition: 'transform 0.2s ease',
  },
  gradeRowExpanded: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: 'none',
  },
  expandedListings: {
    backgroundColor: theme.bg,
    borderLeft: `1px solid ${theme.border}`,
    borderRight: `1px solid ${theme.border}`,
    borderBottom: `1px solid ${theme.border}`,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: 8,
    marginBottom: 6,
  },
  expandedListingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 10px',
    borderRadius: 6,
    backgroundColor: theme.bgCard,
    textDecoration: 'none',
    color: theme.text,
    marginBottom: 4,
  },
  expandedListingPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#22c55e',
    minWidth: 70,
  },
  expandedListingDate: {
    fontSize: 12,
    color: theme.textMuted,
    flex: 1,
  },
  expandedListingSource: {
    fontSize: 10,
    color: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    padding: '2px 6px',
    borderRadius: 4,
    marginRight: 8,
  },
  totalListings: {
    fontSize: 11,
    color: theme.textMuted,
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
  },

  // Listings Section
  listingsSection: {
    marginTop: 20,
    padding: 16,
    backgroundColor: theme.bgCard,
    borderRadius: 12,
    border: `1px solid ${theme.border}`,
  },
  listingsTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.text,
    marginBottom: 12,
  },
  listingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 400,
    overflowY: 'auto',
  },
  listingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    backgroundColor: theme.bg,
    borderRadius: 8,
    textDecoration: 'none',
    color: theme.text,
  },
  listingGrade: {
    fontSize: 12,
    fontWeight: 600,
    color: '#0ea5e9',
    minWidth: 60,
  },
  listingDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  listingPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#22c55e',
  },
  listingDate: {
    fontSize: 11,
    color: theme.textMuted,
  },
  listingTitle: {
    fontSize: 10,
    color: theme.textMuted,
    maxWidth: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  noListings: {
    padding: 24,
    textAlign: 'center',
    color: theme.textMuted,
    fontSize: 13,
  },

  // Chef's Research Report
  researchReport: {
    backgroundColor: theme.bgCard,
    borderRadius: 12,
    padding: 16,
    border: `1px solid ${theme.border}`,
  },
  reportHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  reportChef: {
    width: 60,
    height: 'auto',
    flexShrink: 0,
  },
  reportSpeech: {
    flex: 1,
    paddingTop: 4,
  },
  reportQuote: {
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.text,
    margin: 0,
    fontStyle: 'italic',
  },
  reportMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  reportDateRange: {
    fontSize: 12,
    color: theme.textMuted,
  },

  // ROI Card
  roiCard: {
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    borderRadius: 10,
    padding: 14,
    marginTop: 16,
  },
  roiLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#8b5cf6',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  roiCalc: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '8px 0',
  },
  roiRaw: {
    fontSize: 13,
    color: theme.textMuted,
  },
  roiArrow: {
    fontSize: 13,
    color: theme.textMuted,
  },
  roiGraded: {
    fontSize: 13,
    color: theme.text,
    fontWeight: 500,
  },
  roiProfit: {
    fontSize: 18,
    fontWeight: 700,
    display: 'block',
    marginTop: 4,
  },

  // Info Button & Tooltip
  infoButton: {
    background: 'none',
    border: 'none',
    padding: 2,
    cursor: 'pointer',
    color: theme.textMuted,
    display: 'flex',
    alignItems: 'center',
    opacity: 0.7,
  },
  infoTooltip: {
    marginTop: 10,
    padding: 12,
    backgroundColor: theme.bg,
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
  },
  infoText: {
    fontSize: 12,
    color: theme.textMuted,
    margin: 0,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  infoClose: {
    display: 'block',
    margin: '10px auto 0',
    padding: '6px 16px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    fontSize: 12,
    color: theme.text,
    cursor: 'pointer',
  },
});

export default ChefsLab;
